import { useState } from 'react';
import { Check, Edit2, ShoppingBag, Trash2, X } from 'lucide-react';

import { cn } from '@/lib/cn';
import { fmtInt, fmtUsd } from '@/lib/format';
import {
  useDeletePurchase, useUpdatePurchase, useWinePurchases, type Purchase,
} from './usePurchases';

const TYPES = [
  { value: 'purchase', label: 'Compra' },
  { value: 'gift', label: 'Regalo' },
  { value: 'promo', label: 'Promo' },
  { value: 'sample', label: 'Muestra' },
  { value: 'own_production', label: 'Producción propia' },
];

export function WinePurchasesPanel({ wineId }: { wineId: number }) {
  const { data: purchases, isLoading } = useWinePurchases(wineId);
  const update = useUpdatePurchase();
  const remove = useDeletePurchase();
  const [editingId, setEditingId] = useState<number | null>(null);

  if (isLoading) {
    return (
      <div className="card p-4 md:p-5 text-notion-500 text-sm lg:col-span-3">
        Cargando compras…
      </div>
    );
  }
  if (!purchases || purchases.length === 0) return null;

  return (
    <div className="card p-4 md:p-5 space-y-3 lg:col-span-3">
      <h2 className="font-medium flex items-center gap-2">
        <ShoppingBag size={16} className="text-notion-500" />
        Compras / adquisiciones ({fmtInt(purchases.length)})
      </h2>

      <ul className="divide-y divide-notion-100">
        {purchases.map((p) =>
          editingId === p.id ? (
            <PurchaseEditor
              key={p.id}
              purchase={p}
              onCancel={() => setEditingId(null)}
              onSave={async (patch) => {
                await update.mutateAsync({ id: p.id, patch });
                setEditingId(null);
              }}
              saving={update.isPending}
            />
          ) : (
            <PurchaseRow
              key={p.id}
              purchase={p}
              onEdit={() => setEditingId(p.id)}
              onDelete={async () => {
                if (confirm('¿Eliminar esta compra? Esto borra también el lote asociado.')) {
                  await remove.mutateAsync({ id: p.id, wineId });
                }
              }}
            />
          ),
        )}
      </ul>
    </div>
  );
}

function PurchaseRow({
  purchase, onEdit, onDelete,
}: {
  purchase: Purchase;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const typeLabel =
    TYPES.find((t) => t.value === purchase.acquisition_type)?.label ?? purchase.acquisition_type;

  return (
    <li className="py-2.5 flex flex-wrap items-start justify-between gap-2">
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2 text-sm">
          <strong className="text-notion-800">
            {new Date(purchase.purchase_date).toLocaleDateString('es-AR', {
              day: 'numeric', month: 'short', year: 'numeric',
            })}
          </strong>
          <span className="text-notion-500">·</span>
          <span>{fmtInt(purchase.quantity)} bot</span>
          {purchase.acquisition_type !== 'purchase' && (
            <span
              className={cn(
                'text-[10px] uppercase px-1.5 py-0.5 rounded',
                'bg-amber-50 text-amber-800',
              )}
            >
              {typeLabel}
            </span>
          )}
        </div>
        <div className="text-xs text-notion-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
          {purchase.unit_price != null && purchase.unit_price > 0 && (
            <span>
              {purchase.currency} {fmtInt(purchase.unit_price)}/bot
              {purchase.unit_price_usd ? ` · ${fmtUsd(purchase.unit_price_usd)} USD` : ''}
            </span>
          )}
          {(purchase.supplier?.name || purchase.supplier_text) && (
            <span>
              en <strong className="text-notion-700">{purchase.supplier?.name ?? purchase.supplier_text}</strong>
            </span>
          )}
        </div>
        {purchase.notes && (
          <p className="text-xs text-notion-500 italic mt-0.5">{purchase.notes}</p>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button
          type="button"
          onClick={onEdit}
          className="p-1.5 text-notion-400 hover:text-notion-800"
          aria-label="Editar"
        >
          <Edit2 size={13} />
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="p-1.5 text-notion-400 hover:text-red-600"
          aria-label="Eliminar"
        >
          <Trash2 size={13} />
        </button>
      </div>
    </li>
  );
}

function PurchaseEditor({
  purchase, onCancel, onSave, saving,
}: {
  purchase: Purchase;
  onCancel: () => void;
  onSave: (patch: Partial<Purchase>) => void;
  saving: boolean;
}) {
  const [date, setDate] = useState(purchase.purchase_date);
  const [quantity, setQuantity] = useState(purchase.quantity);
  const [unitPrice, setUnitPrice] = useState(purchase.unit_price ?? 0);
  const [currency, setCurrency] = useState(purchase.currency);
  const [supplier, setSupplier] = useState(purchase.supplier_text ?? purchase.supplier?.name ?? '');
  const [type, setType] = useState(purchase.acquisition_type);
  const [notes, setNotes] = useState(purchase.notes ?? '');

  return (
    <li className="py-3 space-y-3 bg-notion-50/60 px-3 -mx-3 rounded">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="label">Fecha</label>
          <input
            type="date"
            className="input"
            value={date}
            max={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        <div>
          <label className="label">Cantidad</label>
          <input
            type="number"
            min={1}
            inputMode="numeric"
            className="input"
            value={quantity}
            onChange={(e) => setQuantity(Math.max(1, Number(e.target.value)))}
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div className="col-span-2">
          <label className="label">Precio unitario</label>
          <input
            type="number"
            step="0.01"
            inputMode="decimal"
            className="input"
            value={unitPrice}
            onChange={(e) => setUnitPrice(Number(e.target.value))}
          />
        </div>
        <div>
          <label className="label">Moneda</label>
          <select className="input" value={currency} onChange={(e) => setCurrency(e.target.value)}>
            <option value="ARS">ARS</option>
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
            <option value="UYU">UYU</option>
            <option value="CLP">CLP</option>
          </select>
        </div>
      </div>

      <div>
        <label className="label">Lugar de compra</label>
        <input
          className="input"
          placeholder="ej. Sotelo, Ozono Drinks, Bodega directo"
          value={supplier}
          onChange={(e) => setSupplier(e.target.value)}
        />
      </div>

      <div>
        <label className="label">Tipo</label>
        <select
          className="input"
          value={type}
          onChange={(e) => setType(e.target.value as Purchase['acquisition_type'])}
        >
          {TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="label">Notas</label>
        <textarea
          className="input"
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>

      <div className="flex gap-2">
        <button type="button" className="btn-secondary flex-1" onClick={onCancel}>
          <X size={13} /> Cancelar
        </button>
        <button
          type="button"
          className="btn-primary flex-1"
          disabled={saving}
          onClick={() =>
            onSave({
              purchase_date: date,
              quantity,
              unit_price: unitPrice,
              currency,
              supplier_text: supplier.trim() || null,
              acquisition_type: type,
              notes: notes.trim() || null,
            })
          }
        >
          <Check size={13} />
          {saving ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
    </li>
  );
}
